/**
 * PerfMeter — medidor de performance em tempo real (debug), ligado por um toggle
 * nas Configurações. Painel fixo à ESQUERDA, centro vertical. Mostra:
 *
 *   • GPU em uso (WEBGL_debug_renderer_info) — REVELA quando o browser está na
 *     GPU integrada em vez da dedicada (causa clássica de jogo lento em notebook);
 *   • FPS + tempo de frame (média em janela de ~500ms);
 *   • CPU (main thread): updaters + submissão de render, medido no tick;
 *   • GPU (tempo REAL de render, via EXT_disjoint_timer_query_webgl2 — assíncrono,
 *     lido quando o resultado fica pronto; "n/d" se o driver não expõe);
 *   • draw calls + triângulos do frame (renderer.info, acumulado por frame);
 *   • resolução do backbuffer + pixelRatio; entidades; heap JS (Chrome);
 *   • CÂMERA (bloco de baixo): cidade ativa, modo, posição, alvo, distância
 *     ("zoom"), ângulos e fov — é a RÉGUA para reposicionar a câmera na mão
 *     (Config → câmera dinâmica DESLIGADA = câmera livre) e depois anotar os
 *     valores de cada cidade. Clique no bloco copia tudo pronto p/ colar.
 *
 * Integração: SceneManager._tick chama beginGpu()/endGpu() em volta do
 * composer.render() e frame(cpuMs) no fim — SÓ quando `stage.perf` está setado
 * (medidor ligado); desligado, custo zero no loop.
 */

import * as THREE from "three";
import { ACTIVE_CITY } from "../world/cities.js";
import { GRID_FIT } from "../config.js";
import { probes, PROBE_NAMES } from "../perfProbes.js";

const FLUSH_MS = 500; // atualiza o texto do painel 2×/s (sem thrash de DOM)
const CAM_MS = 100; // o bloco da câmera atualiza mais rápido (segue o arraste)

const _off = new THREE.Vector3(); // buffer p/ o vetor alvo→câmera (sem alocar)
const _ORIGIN = new THREE.Vector3(); // alvo de reserva (câmera sem controls)

export class PerfMeter {
    constructor(stage, entities) {
        this.stage = stage;
        this.entities = entities;
        this.renderer = stage.renderer;
        this._gl = this.renderer.getContext();

        // nome da GPU em uso (diagnóstico nº 1 p/ "lento com GPU dedicada")
        let gpu = "";
        try {
            const dbg = this._gl.getExtension("WEBGL_debug_renderer_info");
            gpu = dbg ? this._gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : this._gl.getParameter(this._gl.RENDERER);
        } catch {
            gpu = "?";
        }
        this._gpuName = String(gpu);

        // timer de GPU (WebGL2): mede o tempo REAL de render na placa
        this._ext = null;
        try {
            this._ext = this._gl.getExtension("EXT_disjoint_timer_query_webgl2");
        } catch {
            /* sem timer */
        }
        this._qFree = [];
        this._qPending = [];
        this._qActive = null;
        this._gpuAvg = 0;
        this._perdido = false; // contexto morreu → rearmar o cronômetro (ver _rearmarGpu)

        this._el = null;
        this._elStats = null;
        this._elCam = null;
        this._camText = ""; // último bloco da câmera (é o que o clique copia)
        this._copied = false;
        this._copiedT = 0;
        this._reset();
    }

    _reset() {
        this._last = 0;
        this._ftSum = 0;
        this._cpuSum = 0;
        this._n = 0;
        this._calls = 0;
        this._tris = 0;
        this._flushAt = 0;
        this._camAt = 0;
    }

    enable() {
        if (!this._el) {
            const el = document.createElement("div");
            el.id = "perf-meter";
            // dois blocos: estatísticas (2×/s) e câmera (10×/s, acompanha o arraste)
            this._elStats = document.createElement("div");
            // SUBSISTEMAS (sondas do perfProbes): média/p95/p99/pior + chamadas por
            // quadro, atualizado junto com o bloco de cima (2×/s). Clique = copia o
            // relatório completo (cabeçalho + subsistemas) p/ a área de transferência.
            this._elSub = document.createElement("div");
            this._elSub.id = "perf-meter-sub";
            this._elSub.title = "clique p/ copiar o relatório completo";
            this._elSub.addEventListener("click", () => this._copyReport());
            this._elCam = document.createElement("div");
            this._elCam.id = "perf-meter-cam";
            this._elCam.title = "clique p/ copiar os valores da câmera";
            this._elCam.addEventListener("click", () => this._copyCam());
            el.append(this._elStats, this._elSub, this._elCam);
            document.body.appendChild(el);
            this._el = el;
        }
        this._el.style.display = "block";
        this._elStats.textContent = "coletando…";
        this._reset();
        // acumula os contadores por FRAME COMPLETO (o composer renderiza vários passes;
        // com autoReset cada passe zerava o info — leríamos só o último passe)
        this.renderer.info.autoReset = false;
        this.renderer.info.reset();
        probes.enable(); // sondas por subsistema (desligadas = custo ~zero)
        this._initPassTiming(); // GPU por PASSE do composer (bloom/blur/SMAA/FXAA)
        this.stage.perf = this; // liga os hooks do tick (beginGpu/endGpu/frame)
    }

    disable() {
        this.stage.perf = null;
        this._teardownPassTiming();
        probes.disable();
        this.renderer.info.autoReset = true;
        if (this._el) this._el.style.display = "none";
    }

    /* --------------- GPU POR PASSE do composer (bloom, blur, AA…) --------------- */

    /**
     * Mede o tempo REAL de GPU de CADA passe do EffectComposer, embrulhando o
     * `render` de cada um com uma timer query própria (EXT_disjoint_timer_query:
     * uma query ativa por vez, então elas são SEQUENCIAIS dentro do quadro — o
     * embrulho garante isso porque os passes rodam um após o outro).
     *
     * É o que responde "quanto custa o bloom NESTE perfil, NESTA máquina": o
     * operador troca o perfil no painel e lê a tabela por passe (bloco de baixo
     * das estatísticas). Overlay dos rótulos: já medido à parte pelo par
     * beginGpu/endGpu global (GPUt) menos a soma dos passes.
     *
     * SÓ EXISTE COM O MEDIDOR LIGADO: o embrulho é colocado no enable() e
     * removido no disable() — desligado, os passes ficam com o render original.
     */
    _initPassTiming() {
        if (!this._ext || this._passWrapped) return; // sem timer de GPU → sem tabela
        const composer = this.stage.composer;
        if (!composer) return;
        this._passWrapped = [];
        this._passMs = new Map(); // nome do passe → média móvel (ms)
        const meter = this;
        for (const pass of composer.passes) {
            const nome = pass.constructor?.name || 'Pass';
            const orig = pass.render;
            const dono = { orig, pass, pend: [], livre: [] };
            pass.render = function (...args) {
                if (!pass.enabled) return orig.apply(pass, args);
                meter._passPoll(nome, dono);
                let q = null;
                const gl = meter._gl, ext = meter._ext;
                if (!meter._perdido && dono.pend.length < 4) {
                    try {
                        q = dono.livre.pop() || gl.createQuery();
                        gl.beginQuery(ext.TIME_ELAPSED_EXT, q);
                    } catch { q = null; }
                }
                orig.apply(pass, args);
                if (q) {
                    try { gl.endQuery(ext.TIME_ELAPSED_EXT); dono.pend.push(q); } catch { /* ctx morto */ }
                }
            };
            this._passWrapped.push(dono);
        }
    }

    /** Colhe resultados prontos das queries de UM passe (assíncrono). */
    _passPoll(nome, dono) {
        const gl = this._gl, ext = this._ext;
        if (!dono.pend.length) return;
        try {
            const disjoint = gl.getParameter(ext.GPU_DISJOINT_EXT);
            const q = dono.pend[0];
            const pronto = gl.getQueryParameter(q, gl.QUERY_RESULT_AVAILABLE);
            if (pronto && !disjoint) {
                const ms = gl.getQueryParameter(q, gl.QUERY_RESULT) / 1e6;
                const atual = this._passMs.get(nome) || 0;
                this._passMs.set(nome, atual ? atual * 0.85 + ms * 0.15 : ms);
            }
            if (pronto || disjoint) {
                dono.pend.shift();
                dono.livre.push(q);
            }
        } catch {
            dono.pend.length = 0;
        }
    }

    /** Restaura o render original de todos os passes (medidor desligado). */
    _teardownPassTiming() {
        if (!this._passWrapped) return;
        for (const d of this._passWrapped) d.pass.render = d.orig;
        this._passWrapped = null;
        this._passMs = null;
    }

    /* ---------------- hooks chamados pelo SceneManager._tick ---------------- */

    /**
     * Rearma o cronômetro de GPU depois de uma perda de contexto.
     *
     * Objeto de extensão e queries pertencem ao contexto que morreu: nenhum volta a
     * funcionar, e como todo caminho falha em silêncio a linha de GPU do medidor ficava
     * congelada pelo resto do evento — justo o número que o operador usa para saber se um
     * telão está aguentando as 8 h. Só custa um `getExtension` no contexto novo.
     */
    _rearmarGpu() {
        this._qFree.length = 0;      // queries do contexto morto: descartáveis
        this._qPending.length = 0;
        this._qActive = null;
        this._gpuAvg = 0;
        try {
            this._ext = this._gl.getExtension("EXT_disjoint_timer_query_webgl2");
        } catch {
            this._ext = null;        // sem medição de GPU nesta máquina; o resto segue
        }
    }

    beginGpu() {
        if (this._gl.isContextLost?.()) { this._perdido = true; return; }
        if (this._perdido) { this._perdido = false; this._rearmarGpu(); }
        if (!this._ext || this._qActive || this._qPending.length > 6) return; // não acumula fila
        const gl = this._gl;
        try {
            const q = this._qFree.pop() || gl.createQuery();
            gl.beginQuery(this._ext.TIME_ELAPSED_EXT, q);
            this._qActive = q;
        } catch {
            this._qActive = null;
        }
    }

    endGpu() {
        if (!this._qActive) return;
        try {
            this._gl.endQuery(this._ext.TIME_ELAPSED_EXT);
            this._qPending.push(this._qActive);
        } catch {
            /* contexto perdido etc. */
        }
        this._qActive = null;
    }

    /** Fim do tick: acumula CPU/frame e redesenha o painel a cada FLUSH_MS. */
    frame(cpuMs) {
        const now = performance.now();
        if (this._last) this._ftSum += now - this._last;
        this._last = now;
        this._cpuSum += cpuMs;
        this._n++;
        // contadores do frame que acabou de ser desenhado (acumulados nos passes)
        const ri = this.renderer.info;
        this._calls = ri.render.calls;
        this._tris = ri.render.triangles;
        ri.reset();
        this._pollGpu();
        if (now - this._flushAt >= FLUSH_MS) {
            this._flushAt = now;
            this._redraw();
        }
        if (now - this._camAt >= CAM_MS) {
            this._camAt = now;
            this._redrawCam();
        }
    }

    /** Colhe resultados prontos do timer de GPU (assíncrono, chega frames depois). */
    _pollGpu() {
        const gl = this._gl,
            ext = this._ext;
        if (!ext || !this._qPending.length) return;
        try {
            const disjoint = gl.getParameter(ext.GPU_DISJOINT_EXT);
            const q = this._qPending[0];
            const ready = gl.getQueryParameter(q, gl.QUERY_RESULT_AVAILABLE);
            if (ready && !disjoint) {
                const ms = gl.getQueryParameter(q, gl.QUERY_RESULT) / 1e6;
                this._gpuAvg = this._gpuAvg ? this._gpuAvg * 0.85 + ms * 0.15 : ms; // média móvel
            }
            if (ready || disjoint) {
                this._qPending.shift();
                this._qFree.push(q);
            }
        } catch {
            // consulta a um contexto morto → rearma no próximo quadro (ver _rearmarGpu)
            this._perdido = true;
            this._qPending.length = 0;
        }
    }

    _redraw() {
        const n = Math.max(1, this._n);
        const ft = this._ftSum / n;
        const fps = ft > 0 ? 1000 / ft : 0;
        const cpu = this._cpuSum / n;
        this._ftSum = 0;
        this._cpuSum = 0;
        this._n = 0;

        const buf = this.renderer.domElement;
        const dpr = window.devicePixelRatio || 1;
        const pr = this.renderer.getPixelRatio();
        const heap = performance.memory ? Math.round(performance.memory.usedJSHeapSize / 1048576) + " MB" : "n/d";
        const gpuMs = this._ext ? (this._qPending.length || this._gpuAvg ? this._gpuAvg.toFixed(1) + " ms" : "…") : "n/d";
        const mem = this.renderer.info.memory;

        this._elStats.textContent =
            `GPU   ${this._gpuName.slice(0, 48)}\n` +
            `FPS   ${fps.toFixed(1)}  ·  frame ${ft.toFixed(1)} ms\n` +
            `CPU   ${cpu.toFixed(1)} ms (main thread)\n` +
            `GPUt  ${gpuMs} (render real)\n` +
            `DRAW  ${this._calls} calls  ·  ${fmt(this._tris)} tris\n` +
            `RES   ${buf.width}×${buf.height}  @${pr}  (dpr ${dpr})\n` +
            `CENA  ${mem.geometries} geo · ${mem.textures} tex\n` +
            `JOGO  ${this.entities?.count ?? 0} avatares  ·  heap ${heap}`;

        // SUBSISTEMAS — strings só aqui (2×/s), nunca no caminho do quadro
        let sub = "SUBSISTEMA      avg    p95    p99   pior  ch/q\n";
        for (let i = 0; i < PROBE_NAMES.length; i++) {
            const st = probes.stats(i);
            if (!st.calls && !st.avg) continue;
            sub += PROBE_NAMES[i].padEnd(12) +
                st.avg.toFixed(2).padStart(7) +
                st.p95.toFixed(2).padStart(7) +
                st.p99.toFixed(2).padStart(7) +
                st.worst.toFixed(2).padStart(7) +
                st.calls.toFixed(0).padStart(6) + "\n";
        }
        // GPU POR PASSE (bloom/blur/AA) — só existe se o driver expõe o timer.
        // O overlay dos rótulos = GPUt global − soma dos passes (ele roda fora
        // do composer de propósito; ver render-performance).
        if (this._passMs && this._passMs.size) {
            sub += "— GPU por passe (perfil " + (this.stage.quality || '?') + ") —\n";
            let soma = 0;
            for (const [nome, ms] of this._passMs) {
                soma += ms;
                sub += nome.replace('Pass', '').padEnd(12) + ms.toFixed(2).padStart(7) + " ms\n";
            }
            const overlayMs = Math.max(0, (this._gpuAvg || 0) - soma);
            sub += "overlay+resto".padEnd(12) + overlayMs.toFixed(2).padStart(7) + " ms\n";
        }
        sub += this._copiedSub ? "✓ copiado" : "⧉ clique p/ copiar o relatório";
        this._elSub.textContent = sub;
    }

    /** Relatório completo (cabeçalho + subsistemas) → área de transferência. */
    _copyReport() {
        const cab = this._elStats.textContent;
        const texto = `${cab}

${probes.report()}
`;
        try {
            navigator.clipboard?.writeText(texto);
            this._copiedSub = true;
            clearTimeout(this._copiedSubT);
            this._copiedSubT = setTimeout(() => { this._copiedSub = false; }, 1500);
            this._redraw();
        } catch {
            /* sem clipboard — o texto continua na tela */
        }
    }

    /**
     * Bloco da CÂMERA — a régua p/ reposicionar a vista de cada cidade na mão.
     * Tudo é derivado da câmera REAL do frame (posição e vetor alvo→câmera), então
     * vale igual nos três modos (dinâmica, livre e perseguição) — não depende do
     * OrbitControls estar no comando.
     */
    _redrawCam() {
        const s = this.stage;
        const cam = s.camera;
        if (!cam) return;
        // ALVO: na perseguição é o ponto visado suavizado; nos outros dois é o alvo do
        // OrbitControls (a câmera dinâmica escreve o dela lá todo frame).
        const tgt = (s.followCam && s._followAim) || s.controls?.target || _ORIGIN;
        const p = cam.position;

        _off.subVectors(p, tgt);
        const dist = _off.length();
        // polar = ângulo a partir do ZÊNITE (0° = de cima a pino, 90° = horizonte);
        // elev = complemento (quanto a câmera está ACIMA do horizonte, leitura direta);
        // azim = giro em torno do eixo Y na convenção do OrbitControls (0° = câmera no +Z).
        const polar = dist > 1e-4 ? (Math.acos(Math.min(1, Math.max(-1, _off.y / dist))) * 180) / Math.PI : 0;
        const azim = (Math.atan2(_off.x, _off.z) * 180) / Math.PI;

        const modo = s.followCam ? "perseguição" : s.dynamicCam ? "dinâmica" : "LIVRE (manual)";
        // "zoom" = distância relativa à vista padrão de carregamento (CAMERA.position da
        // config). 100% = vista padrão; <100% = mais perto; >100% = mais longe.
        const base = s._baseDistMax || dist;
        const zoom = (dist / base) * 100;
        // fator de recuo por proporção de tela (mobile recua; no desktop é 1 — ver
        // SceneManager._applyAspectFraming). Só aparece quando ≠ 1 p/ não poluir.
        const k = s._camDistMax && s._baseDistMax ? s._camDistMax / s._baseDistMax : 1;
        const kTxt = Math.abs(k - 1) > 0.005 ? `  ·  recuo de tela ×${k.toFixed(2)}` : "";
        const cols = ACTIVE_CITY.cols ?? 5,
            rows = ACTIVE_CITY.rows ?? 3;

        const pos = `[${n1(p.x)}, ${n1(p.y)}, ${n1(p.z)}]`;
        const aim = `[${n1(tgt.x)}, ${n1(tgt.y)}, ${n1(tgt.z)}]`;

        // texto copiável (o clique manda ISTO p/ a área de transferência)
        this._camText = `city: '${ACTIVE_CITY.id}'  (${cols}×${rows}, GRID_FIT ${GRID_FIT.toFixed(3)})\n` + `position: ${pos},\n` + `target: ${aim},\n` + `fov: ${cam.fov},  dist: ${n1(dist)},  polar: ${polar.toFixed(1)}°,  azim: ${azim.toFixed(1)}°`;

        this._elCam.textContent =
            `CIDADE ${ACTIVE_CITY.id}  ·  ${cols}×${rows}  ·  fit ${GRID_FIT.toFixed(2)}\n` +
            `MODO   ${modo}\n` +
            `POS    ${pos}\n` +
            `ALVO   ${aim}\n` +
            `DIST   ${n1(dist)} u  ·  zoom ${zoom.toFixed(0)}% da vista padrão${kTxt}\n` +
            `ÂNG    polar ${polar.toFixed(1)}°  ·  elev ${(90 - polar).toFixed(1)}°  ·  azim ${azim.toFixed(1)}°\n` +
            `LENTE  fov ${cam.fov}°  ·  aspect ${cam.aspect.toFixed(3)}\n` +
            `${this._copied ? "✓ copiado" : "⧉ clique aqui p/ copiar"}`;
    }

    /** Clique no bloco da câmera → área de transferência (p/ colar no chat/config). */
    _copyCam() {
        if (!this._camText) return;
        try {
            navigator.clipboard?.writeText(this._camText);
            this._copied = true;
            clearTimeout(this._copiedT);
            this._copiedT = setTimeout(() => {
                this._copied = false;
            }, 1500);
            this._redrawCam();
        } catch {
            /* sem permissão de clipboard — o texto continua legível na tela */
        }
    }
}

function fmt(n) {
    return n >= 1e6 ? (n / 1e6).toFixed(2) + "M" : n >= 1e3 ? (n / 1e3).toFixed(1) + "k" : String(n);
}

/** Uma casa decimal, sem "-0.0" (ruído visual em valores centrados no zero). */
function n1(v) {
    const s = v.toFixed(1);
    return s === "-0.0" ? "0.0" : s;
}
